// Shared by the editor and the baked track painter. All measurements are in world
// units: resizing adds seats and wall sections, never enlarges a spectator.
export const STAND_SOURCE = 'materials/stands/1.png';
const SCALE = 0.4;
const cache = new WeakMap();

// Separate people from the original stand, including their own shaded silhouette.
// These crops deliberately avoid flags, umbrellas and neighbouring spectators.
const PEOPLE = [
  [62, 57, 25, 34], [95, 57, 25, 34], [124, 59, 25, 34],
  [164, 76, 25, 34], [218, 74, 25, 34], [285, 58, 25, 34],
  [386, 56, 25, 34], [475, 74, 25, 34], [504, 99, 25, 34],
];

function spectators(image, createCanvas) {
  if (cache.has(image)) return cache.get(image);
  if (!createCanvas) return [];
  const sprites = PEOPLE.map(([x, y, w, h]) => {
    const canvas = createCanvas(w, h), ctx = canvas.getContext('2d');
    // A narrow head and broad shoulders/seat; exclude the seating around them.
    ctx.beginPath();
    ctx.moveTo(8, 3);
    ctx.bezierCurveTo(11, 0, 18, 1, 19, 7);
    ctx.bezierCurveTo(19, 10, 18, 11, 20, 13);
    ctx.bezierCurveTo(27, 17, 25, 29, 19, 32);
    ctx.bezierCurveTo(12, 35, 2, 30, 1, 23);
    ctx.bezierCurveTo(0, 17, 5, 14, 7, 12);
    ctx.bezierCurveTo(5, 8, 6, 5, 8, 3);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(image, x, y, w, h, 0, 0, w, h);
    return canvas;
  });
  cache.set(image, sprites);
  return sprites;
}

function choice(seed, row, column) {
  let n = Math.imul((seed | 0) ^ Math.imul(row + 1, 374761393) ^ Math.imul(column + 1, 668265263), 1274126177);
  n ^= n >>> 16;
  return n >>> 0;
}

/** A regular, staggered crowd with deterministic random people per seat. */
export function standSeats(width, height, seed = 0) {
  const seats = [];
  const marginX = 22, marginY = 32, stepX = 18, stepY = 17;
  const columns = Math.max(0, Math.floor((width - marginX * 2) / stepX) + 1);
  const rows = Math.max(0, Math.floor((height - marginY * 2) / stepY) + 1);
  if (!columns || !rows) return seats;
  const x0 = -(columns - 1) * stepX / 2, y0 = -(rows - 1) * stepY / 2;
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      const random = choice(seed, row, column);
      const x = x0 + column * stepX + (row % 2 ? 3 : -3);
      seats.push({ x, y: y0 + row * stepY, variant: random % PEOPLE.length });
    }
  }
  return seats;
}

/** Draw a stand centred at the origin, in the caller's local world transform. */
export function drawStand(ctx, width, height, image, createCanvas, seed = 0) {
  if (!(width > 0 && height > 0)) return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(-width / 2, -height / 2, width, height);
  ctx.clip();
  ctx.fillStyle = '#3c4531';
  ctx.fillRect(-width / 2, -height / 2, width, height);
  if (!image) { ctx.restore(); return; }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const left = -width / 2, top = -height / 2;
  // For exceptionally small rectangles only, keep corners from overlapping.
  const scale = Math.min(SCALE, width / 120, height / 140);
  const stamp = (sx, sy, sw, sh, x, y, w = sw * scale, h = sh * scale) =>
    ctx.drawImage(image, sx, sy, sw, sh, x, y, w, h);
  // Empty weathered seating strip from the source, tiled without stretching.
  const tileW = 480 * SCALE, tileH = 10 * SCALE;
  for (let y = top; y < top + height; y += tileH) {
    for (let x = left; x < left + width; x += tileW) {
      ctx.drawImage(image, 40, 40, 480, 10, x, y, tileW, tileH);
    }
  }
  // Horizontal seat risers, behind the crowd.
  ctx.fillStyle = 'rgba(12,18,10,0.32)';
  // 3 px, not 2: the sprite stands for a few hundred world units, so a 2 px step of the stairs is one
  // unit of world — under a texel of the track picture, where it flickers (see fenceLayout.MIN_BAND).
  for (let y = top + 26; y < top + height - 12; y += 17) ctx.fillRect(left + 12, y, Math.max(0, width - 24), 3);
  // The complete LEFT rail is the source for every side. The old right crop
  // began halfway through its rail. Mirror horizontally, never rotate flags.
  const topRail = top + 50 * scale, bottomRail = top + height - 20 * scale;
  const leftRail = left + 19 * scale, rightRail = left + width - 19 * scale;
  function verticalRail(x, mirror) {
    ctx.save();
    ctx.translate(x, 0);
    if (mirror) ctx.scale(-1, 1);
    for (let y = topRail; y < bottomRail; y += 130 * scale) {
      const h = Math.min(130 * scale, bottomRail - y);
      stamp(0, 80, 34, h / scale, 0, y);
    }
    ctx.restore();
  }
  verticalRail(left, false);
  verticalRail(left + width, true);
  // Turn the same full rail sideways to close the top and bottom. Corner posts
  // cover the joins, so both rails meet the side walls without open ends.
  function horizontalRail(y) {
    ctx.save();
    ctx.translate(leftRail, y + 19 * scale);
    ctx.rotate(-Math.PI / 2);
    for (let x = 0; x < rightRail - leftRail; x += 130 * scale) {
      const w = Math.min(130 * scale, rightRail - leftRail - x);
      stamp(0, 80, 34, w / scale, 0, x);
    }
    ctx.restore();
  }
  horizontalRail(topRail);
  horizontalRail(bottomRail);
  for (const y of [topRail, bottomRail]) {
    for (const mirrored of [false, true]) {
      ctx.save();
      ctx.translate(mirrored ? left + width : left, 0);
      if (mirrored) ctx.scale(-1, 1);
      stamp(0, 50, 34, 30, 0, y - 14 * scale);
      // Pole and pennant only, separated from the source's corner/wall. Both
      // lower flags keep exactly the same upright orientation as the upper pair.
      stamp(8, 0, 56, 38, 12 * scale, y - 38 * scale);
      ctx.restore();
    }
  }
  const sprites = spectators(image, createCanvas);
  for (const seat of standSeats(width, height, seed)) {
    const sprite = sprites[seat.variant];
    if (sprite) ctx.drawImage(sprite, seat.x - 5, seat.y - 6.8, 10, 13.6);
  }
  ctx.restore();
}
